import React, { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { Check, ChevronDown, X } from "lucide-react";

const ChoicePopupPicker = ({
  options = [],
  value,
  onChange,
  onClear,
  placeholder = "Select an option",
  disabled = false,
  allowClear = false,
}) => {
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraftValue(value ?? null);
  }, [open, value]);

  const hasSelection = Boolean(value);

  const handleConfirm = () => {
    onChange(draftValue);
    setOpen(false);
  };

  const modalWidth =
    typeof window !== "undefined"
      ? Math.max(window.innerWidth - 16, 320)
      : 360;

  return (
    <>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="flex h-11 min-w-0 flex-1 items-center justify-between rounded-xl border border-[#d9d9d9] bg-white px-3 text-left text-base shadow-sm transition-colors hover:border-[#4096ff] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span
            title={value || undefined}
            className={`min-w-0 flex-1 truncate pr-2 ${
              hasSelection ? "text-gray-900" : "text-gray-400"
            }`}
          >
            {value || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        </button>
        {allowClear && hasSelection && !disabled ? (
          <button
            type="button"
            onClick={onClear}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#d9d9d9] bg-white text-gray-500"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <Modal
        open={open}
        title={placeholder}
        onCancel={() => setOpen(false)}
        footer={[
          <Button
            key="done"
            type="primary"
            className="w-full rounded-lg"
            onClick={handleConfirm}
          >
            Submit
          </Button>,
        ]}
        width={modalWidth}
        centered
        destroyOnClose
        className="choice-popup-picker-modal"
        styles={{
          body: {
            maxHeight: "min(70vh, 520px)",
            overflowY: "auto",
            paddingTop: 8,
          },
        }}
      >
        <div className="flex flex-col gap-2 pb-2">
          {options.map((option, idx) => {
            const selected = draftValue === option;

            return (
              <button
                key={`${option}-${idx}`}
                type="button"
                onClick={() => setDraftValue(option)}
                className={`flex w-full items-center gap-3 rounded-xl border bg-white px-3 py-3 text-left transition-colors hover:bg-gray-50 ${
                  selected
                    ? "border-2 border-gray-600 shadow-sm"
                    : "border border-gray-200"
                }`}
              >
                <span className="min-w-0 flex-1 whitespace-normal text-sm leading-snug text-gray-800 sm:text-base">
                  {option}
                </span>
                {selected ? (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-700 text-white">
                    <Check size={12} strokeWidth={3} />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default ChoicePopupPicker;
